import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslations } from 'use-intl';
import { FirebaseContext, LocalContext } from 'context';
import { AnimatePresence } from 'motion/react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Button, Message } from 'components';
import { navigate, useStaticQuery, graphql } from 'gatsby';

function LoginForm() {
  const t = useTranslations('Auth');
  const { firebase, user } = useContext(FirebaseContext);
  const { showLoginDialog, setShowLoginDialog, handleDialog, loginDialogRef } =
    useContext(LocalContext);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [email, setEmail] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const data = useStaticQuery(graphql`
    query {
      allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/(src/data/sessions)/" } }
      ) {
        nodes {
          frontmatter {
            id
            slug
          }
        }
      }
    }
  `);

  const sessions = data.allMarkdownRemark.nodes.map((node) => node.frontmatter);

  useEffect(() => {
    if (firebase && user && user.sessionsUserHasAccessTo?.length > 0) {
      // Use the first session from user's accessible sessions
      const defaultSessionId = user.sessionsUserHasAccessTo[0];

      const defaultSession = sessions.find(
        (session) => session.id === defaultSessionId
      );

      if (defaultSession) {
        navigate(`/sessions/${defaultSession.slug}`);
      }
    }
  }, [firebase, user, sessions]);

  const handleInputChange = (e) => {
    e.persist();

    if (errorMessage) {
      setErrorMessage('');
    }

    setEmail(e.target.value);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (email && consentGiven && firebase && firebase.auth) {
      setSubmittingForm(true);
      try {
        const { data: pw } =
          await firebase.userProfile.checkIfUserCanLogInDirectly({ email });
        await signInWithEmailAndPassword(
          firebase.auth,
          email.toLowerCase().trim(),
          pw
        );
        if (showLoginDialog) {
          handleDialog({
            dialogRef: loginDialogRef,
            animation: 'close',
            stateHandler: () => setShowLoginDialog(false)
          });
        }
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
        setSubmittingForm(false);
      }
    }
  };

  return (
    <Container>
      <h2>{t('loginTitle')}</h2>
      <form onSubmit={handleFormSubmit}>
        <label label="email">
          <input
            name="email"
            type="email"
            onChange={handleInputChange}
            value={email}
            placeholder={t('emailPlaceholder')}
            autoFocus
            required
          />
        </label>
        <ConsentLabel htmlFor="consent">
          <ConsentInput
            id="consent"
            name="consent"
            type="checkbox"
            checked={consentGiven}
            onChange={(e) => setConsentGiven(e.target.checked)}
            required
          />
          <ConsentBox $checked={consentGiven} aria-hidden="true">
            <svg viewBox="0 0 15 13" fill="none">
              <path
                d="M0.920898 6.92947L5.4209 10.4295L12.9209 0.929474"
                stroke="currentColor"
                strokeWidth="3"
              />
            </svg>
          </ConsentBox>
          <span>{t('consentText')}</span>
        </ConsentLabel>
        <Button
          type="submit"
          loading={submittingForm}
          disabled={!consentGiven}
          changeColorWhenDisabled={false}
          shine>
          {t('loginButton')}
        </Button>
      </form>
      <AnimatePresence>
        {errorMessage && (
          <Message
            type="error"
            message={
              errorMessage === 'Firebase: Error (auth/user-disabled).'
                ? t('sessionEnded')
                : errorMessage
            }
          />
        )}
      </AnimatePresence>
    </Container>
  );
}

const Container = styled.section`
  background-color: #fff;
  border: 1px solid #e2e4ea;
  border-radius: var(--border-radius-md);
  margin: 0 auto;
  max-width: 20.938rem;
  padding: 3.25rem 1.25rem 2.5rem;
  perspective: 50rem;
  position: relative;
  width: 100%;
  z-index: 1;

  @media (min-width: 1200px) {
    max-width: 28.375rem;
    padding: 3.25rem 1.875rem 2.5rem;
  }

  h2,
  p {
    text-align: center;
  }

  p {
    margin-bottom: 1.65em;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1em;
    text-transform: uppercase;
  }

  form {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  }

  label,
  input {
    width: 100%;
  }

  label {
    margin-bottom: 1.188rem;

    &:last-of-type {
      margin-bottom: 2.05rem;
    }
  }
`;

const ConsentLabel = styled.label`
  align-items: flex-start;
  cursor: pointer;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem !important;
  text-align: left;

  span {
    color: var(--light-brown);
    font-size: 0.8125rem;
    line-height: 1.4em;
  }
`;

const ConsentInput = styled.input`
  height: 1px !important;
  opacity: 0;
  position: absolute;
  width: 1px !important;

  &:focus-visible + span {
    outline: 2px solid var(--blue);
    outline-offset: 2px;
  }
`;

const ConsentBox = styled.span`
  align-items: center;
  background: #fff;
  border: 1.5px solid var(--light-grey);
  border-radius: 0.25rem;
  display: flex;
  flex-shrink: 0;
  height: 2rem;
  justify-content: center;
  transition: border-color 0.2s;
  width: 2rem;

  svg {
    color: var(--blue);
    height: 0.95rem;
    margin-left: 2px;
    margin-top: 1px;
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
    transition: opacity 0.15s;
    width: 1.1rem;
  }
`;

export default LoginForm;
